import Head from 'next/head'
import { StaticImageData } from 'next/image'
import { PillarPage } from 'src/constants/searchPages'
import heroParking from '../../../public/images/estacionamiento-hero-desktop.webp'
import heroParkingMobile from '../../../public/images/estacionamiento-mobile.webp'
import heroBodegasMobile from '../../../public/images/personalized-bodegas-mobile.webp'
import heroBodegas from '../../../public/images/personalized-bodegas.jpg'
import heroMiniBodegasMobile from '../../../public/images/personalized-minibodegas-mobile.webp'
import heroMiniBodegas from '../../../public/images/personalized-minibodegas.jpg'
import heroNaveMobile from '../../../public/images/personalized-nave-mobile.webp'
import heroNave from '../../../public/images/personalized-nave.webp'

/**
 * Shared photo plumbing for the two pillar heroes (`Hero` = organic,
 * `HeroPersonalized` = paid `?city=`), so both variants and every pillar
 * render the same masthead treatment and preload the same files.
 *
 * Desktop keeps the framed right-rail card. Mobile (≈85% of pillar entries)
 * gets a short photo band under the nav, capped at 176px so the first
 * action stays close to the heading. The home hero keeps its background
 * photo behind the copy. The band fades into the cream the copy sits
 * on and the copy block pulls up into that fade.
 *
 * Images are CSS backgrounds, NOT `<Image fill>` — see CLAUDE.md "Trigger J"
 * (the `fill` inline style block + iOS Safari cached-image fast path fires
 * React #418). `suppressHydrationWarning` covers the `url(x)` vs `url("x")`
 * drift ("Trigger D Phase 5"). Both are decorative, so `aria-hidden` stands in
 * for `alt`.
 */

/** Desktop card sources. Parking is a 1200×1200 webp crop (97 KB) of the
 *  581 KB `estacionamiento-1600-blue.jpg` the card used to load — the card
 *  renders ≤600px square, and the preload below now fetches this file at
 *  high priority on every desktop parking landing. The jpg stays for the
 *  cross-pillar tiles that still reference it. */
export const PILLAR_HERO_IMAGE: Record<PillarPage, StaticImageData> = {
  [PillarPage.SelfStorage]: heroMiniBodegas,
  [PillarPage.Parking]: heroParking,
  [PillarPage.Warehouse]: heroBodegas,
  [PillarPage.Industrial]: heroNave
}

/** 900×560 webp crops of the same photos for the mobile band (23–79 KB).
 *  Regenerate with `magick <src> -resize 900x560^ -gravity center -extent
 *  900x560 -strip -quality 78 -define webp:method=6 <out>` — the nave crop
 *  uses `-quality 58` (steel racking compresses badly; 106 KB at 78). */
export const PILLAR_HERO_IMAGE_MOBILE: Record<PillarPage, StaticImageData> = {
  [PillarPage.SelfStorage]: heroMiniBodegasMobile,
  [PillarPage.Parking]: heroParkingMobile,
  [PillarPage.Warehouse]: heroBodegasMobile,
  [PillarPage.Industrial]: heroNaveMobile
}

/** Mirrors the Tailwind `md` breakpoint (900px) used by the layout below. */
const MOBILE_MEDIA = '(max-width: 899px)'
const DESKTOP_MEDIA = '(min-width: 900px)'

interface IPillarProps {
  pillar: PillarPage
}

/**
 * The hero photo is the LCP candidate on both breakpoints and, being a CSS
 * background, is only discovered once the element is laid out. Preload one
 * file per breakpoint so the fetch starts with the HTML.
 */
export const HeroImagePreload = ({ pillar }: IPillarProps) => (
  <Head>
    <link
      rel="preload"
      as="image"
      href={PILLAR_HERO_IMAGE_MOBILE[pillar].src}
      media={MOBILE_MEDIA}
      fetchPriority="high"
    />
    <link
      rel="preload"
      as="image"
      href={PILLAR_HERO_IMAGE[pillar].src}
      media={DESKTOP_MEDIA}
      fetchPriority="high"
    />
  </Head>
)

interface IBadgeProps extends IPillarProps {
  /** Small caps caption over the photo — "México · en vivo" / "{{city}} · en vivo". */
  badge: string
}

/**
 * MOBILE masthead — photo band directly under the nav. Height is capped so
 * the H1 and primary CTA still land inside the first viewport on a 667px
 * screen with the size + on-demand chips mounted. The bottom of the band
 * dissolves into `cream-300`; a light top scrim keeps the badge legible on
 * bright photos.
 */
export const HeroMobileBand = ({ pillar, badge }: IBadgeProps) => (
  <div className="relative h-[min(22vh,176px)] min-h-[128px] md:hidden">
    <div
      aria-hidden="true"
      className="absolute inset-0 bg-cover bg-[50%_55%] bg-no-repeat"
      style={{
        backgroundImage: `url("${PILLAR_HERO_IMAGE_MOBILE[pillar].src}")`
      }}
      suppressHydrationWarning
    />
    <div className="absolute inset-0 bg-gradient-to-b from-ink-800/35 via-transparent via-40% to-cream-300" />
    <span className="absolute top-4 left-5 font-display text-[11px] tracking-[0.2em] text-white-500/85 uppercase">
      {badge}
    </span>
  </div>
)

/**
 * DESKTOP framed photo card for the right rail. Square so it doesn't
 * dominate the copy rail vertically; the stat `<dl>` under it stays in the
 * calling hero because the two variants show different figures.
 */
export const HeroPhotoCard = ({ pillar, badge }: IBadgeProps) => (
  <div className="relative aspect-square w-full overflow-hidden">
    <div
      aria-hidden="true"
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url("${PILLAR_HERO_IMAGE[pillar].src}")` }}
      suppressHydrationWarning
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink-800/50 via-transparent to-transparent" />
    <span className="absolute top-5 left-5 font-display text-xs tracking-[0.2em] text-white-500/80 uppercase">
      {badge}
    </span>
  </div>
)

/** Primary-action pill shared by both heroes. `blue-500` + the blue glow is
 *  the home hero's chrome, with white labels. The brand entry and
 *  pillar entries read as one system. */
export const HERO_PRIMARY_CTA_CLASS =
  'group inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-500 px-7 py-3 text-center text-base font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98] md:w-auto md:px-8 md:text-[17px] md:whitespace-nowrap'

/** Sibling-product chip (on-demand on storage, flotillas on parking). */
export const HERO_SIBLING_CHIP_CLASS =
  'group inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-white-500 px-3.5 py-1.5 text-[13px] font-medium text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500'
